import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { LeavingSiteModal } from '@components/LeavingSiteModal';
import { CoreBox, CoreTypography } from '@youscience/core';
import { ScholarshipDetail } from '@common-types';

import { Breadcrumbs } from '@components/Breadcrumbs';
import { CardSlider, CardType } from '@components/CardSlider';

import { baseService } from '@services/base.service';

import { CollegeLocation } from '@interfaces/location';

import { BREADCRUMBS_PAGE_TEXT } from '@constants/breadcrumbs';
import { AWARD_TYPE_ICONS } from '@constants/scholarships';
import { ROUTES } from '@constants/ROUTES';

import { getBreadcrumbs, getFromScholarshipLocation } from '@utils/breadcrumbs';

import AboutSection from './components/AboutSection';
import Locations from './components/Locations';
import Requirements from './components/Requirements';
import AdditionallySection from './components/AdditionallySection';
import AreasOfStudySection from './components/AreasOfStudySection';
import HeaderActions from './components/HeaderActions';
import StatisticsSection from './components/StatisticsSection';

import { sxStyles, StyledHeadSectionInner } from './ScholarshipDetails.styles';

export const ScholarshipDetails = () => {
  const { id = '' } = useParams();
  const location: CollegeLocation = useLocation();

  const [scholarship, setScholarship] = useState<ScholarshipDetail>({} as ScholarshipDetail);
  const [isOpenLeavingSiteModal, setIsOpenLeavingSiteModal] = useState(false);

  const { awardType, programName, donorName, webAddress, areasOfStudy, schools } = scholarship;

  const pageBreadcrumbs = getBreadcrumbs(location.state?.from, BREADCRUMBS_PAGE_TEXT.SCHOLARSHIP_DETAILS);

  useEffect(() => {
    let isCurrent = true;

    const getScholarshipData = async () => {
      const scholarshipData = await baseService.getAsync<ScholarshipDetail>(`/scholarship/${id}`);

      if (isCurrent) setScholarship(scholarshipData);
    };

    void getScholarshipData();

    return () => {
      isCurrent = false;
    };
  }, [id]);

  const handleOnCancelLeavingSiteModal = () => {
    setIsOpenLeavingSiteModal(false);
  };

  const handleOnContinueLeavingSiteModal = () => {
    if (!webAddress?.programUrl) return;

    window.open(webAddress.programUrl, '_blank', 'noopener,noreferrer');

    setIsOpenLeavingSiteModal(false);
  };

  return (
    <>
      <CoreBox sx={sxStyles.pageBg}>
        <CoreBox sx={sxStyles.headSectionBg}>
          <StyledHeadSectionInner>
            <Breadcrumbs links={pageBreadcrumbs.links} currentPage={pageBreadcrumbs.currentPage} />

            <CoreBox sx={sxStyles.headSectionInfo}>
              <CoreBox sx={sxStyles.awardType}>
                {AWARD_TYPE_ICONS[awardType]}

                <CoreTypography variant="overline" component="h6">
                  {awardType}
                </CoreTypography>
              </CoreBox>

              <CoreTypography sx={sxStyles.programName} variant="h2" component="h1">
                {programName}
              </CoreTypography>

              {donorName && <CoreTypography variant="caption">{`Offered by ${donorName}`}</CoreTypography>}
            </CoreBox>

            <HeaderActions
              scholarshipId={+id}
              webAddress={webAddress?.programUrl}
              onApplyScholarship={() => setIsOpenLeavingSiteModal(true)}
            />
          </StyledHeadSectionInner>
        </CoreBox>

        <AboutSection scholarship={scholarship} />

        <Requirements scholarship={scholarship} />

        <StatisticsSection scholarship={scholarship} />

        <AdditionallySection scholarship={scholarship} />

        {areasOfStudy?.length > 0 && <AreasOfStudySection areas={areasOfStudy} />}

        <Locations stateResidence={scholarship.stateResidence} stateRestrictions={scholarship.stateRestrictions} />

        {schools?.length > 0 && (
          <CoreBox sx={sxStyles.relatedSchoolsBg}>
            <CardSlider
              dataTestId="matchedSchools"
              cardType={CardType.SCHOOL}
              title={`Colleges that match with this ${awardType.toLowerCase()}`}
              cardList={schools}
              navigateLink={ROUTES.SCHOOLS}
              cameFrom={getFromScholarshipLocation(location.pathname)}
              buttonTitle="Explore more"
            />
          </CoreBox>
        )}
      </CoreBox>

      {isOpenLeavingSiteModal && (
        <LeavingSiteModal
          isOpenLeavingSiteModal={isOpenLeavingSiteModal}
          website={webAddress?.programUrl}
          onCancel={handleOnCancelLeavingSiteModal}
          onContinue={handleOnContinueLeavingSiteModal}
        />
      )}
    </>
  );
};
